import { useState } from 'react';
import { TopNav } from './nav/TopNav';
import { MobileNavOverlay } from './nav/MobileNavOverlay';
import { EvieChat } from '@/components/evie/EvieChat';
import { TermsGate, TermsUpdateBanner } from '@/components/TermsGate';
import { OnboardingBanner } from '@/pages/partner-jobs/WelcomePage';
import { BankSetupBanner } from '@/components/site/BankSetupBanner';
import { useEvieStore } from '@/store/evie';
import { useAuthStore } from '@/store/auth';
import styles from './AppShell.module.css';

interface AppShellProps {
  children: React.ReactNode;
  currentPath?: string;
  onNavigate?: (path: string) => void;
}

/**
 * Admin Panel shell. As of the dashboard-redesign rebuild this is a
 * thin chrome: a horizontal glassmorphic top nav (TopNav) + a
 * <main> for page content. No left sidebar, no icon rail, no
 * desktop drawer.
 *
 * Mobile/tablet (<1024px): the hamburger button on TopNav opens
 * MobileNavOverlay (slide-in from the right).
 *
 * Nav data lives in config/nav.ts (NAV_SECTIONS) — single source
 * of truth shared by TopNav and MobileNavOverlay. The pre-rebuild
 * inline `NAV` array that sat at the top of this file was deleted
 * during the redesign; everything now reads from NAV_SECTIONS.
 *
 * The legacy IconRail / Flyout / FlyoutCascade / RecentsFlyout
 * components in components/ui/nav/ are now orphans — they're no
 * longer mounted but their files stay in the tree to keep this
 * diff focused on the user-visible change. A follow-up commit
 * can sweep the orphans (and the matching CSS modules) out.
 */
export function AppShell({ children, currentPath = '/dashboard', onNavigate }: AppShellProps) {
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleNavigate = (path: string) => {
    if (onNavigate) {
      onNavigate(path);
    } else {
      window.location.href = path;
    }
  };

  // Dashboard is the only surface that pins its content column to the
  // viewport's left edge instead of auto-centering at 1400px. On wide
  // monitors the centered default leaves a large dead band between the
  // top nav and the first KPI card; left-pinning anchors the L-layout
  // flush to the page gutter. Other pages keep the centered .content
  // for readable line lengths.
  const isDashboard = currentPath === '/dashboard';

  // When EVII is maximized to the docked side panel, the main content reflows
  // into the narrower remaining space (margin-right) instead of being covered.
  const eviePanelDocked = useEvieStore((s) => s.view === 'panel');
  // Building staff (lib facilitiesOnlyGate in the Api): no bank or onboarding banner, no EVII.
  const buildingStaff = useAuthStore((s) => s.user?.facilities_only === true);

  // Brand-mark click destination is role-aware (fleet_admin → /fleets,
  // everyone else → /dashboard). That logic lives inside TopNav where
  // the click handler is wired; the shell itself doesn't need the
  // role here.

  return (
    <div className={styles.shell}>
      <TopNav
        currentPath={currentPath}
        onNavigate={handleNavigate}
        onOpenMobileMenu={() => setMobileOpen(true)}
      />

      <MobileNavOverlay
        open={mobileOpen}
        currentPath={currentPath}
        onClose={() => setMobileOpen(false)}
        onNavigate={handleNavigate}
      />

      <main
        data-admin-content
        className={`${styles.content} ${isDashboard ? styles.contentLeftPinned : ''} ${eviePanelDocked ? styles.contentWithEvie : ''}`}
      >
        {/* Building staff see no money (Warren, 28 Oct 2026, item 5). */}
        {!buildingStaff && <BankSetupBanner />}
        {/* A non-material terms update: a banner, never a gate (Warren, 21 October 2026). */}
        <TermsUpdateBanner />
        {/* A New job customer with setup steps left (Warren, 21 October 2026). */}
        {!buildingStaff && <OnboardingBanner onNavigate={handleNavigate} />}
        {children}
      </main>

      {/* Persistent EVII chat shell (popup / docked side panel). Presentation
          only — it calls just the proven scoped /admin-assistant endpoint. */}
      {!buildingStaff && <EvieChat />}

      {/* Blocking terms-acceptance gate — every admin user accepts the current
          platform Terms of Service; a version bump re-triggers it. Portals to
          body and renders nothing once accepted. */}
      <TermsGate />
    </div>
  );
}
